import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getImageUrl } from '../utils/imageUrl';
import './AdminDashboard.css';

const TYPES = ['youtube', 'instagram', 'blog'];

function AdminDashboard() {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [activeType, setActiveType] = useState('youtube');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        TYPES.map(type =>
          axios.get(`/api/submissions/admin/${type}`, {
            headers: authHeader
          }).then(r => ({ type, data: r.data }))
        )
      );

      const all = {};
      const c = {};
      results.forEach(({ type, data }) => {
        all[type] = data;
        c[type] = {
          total: data.length,
          pending: data.filter(s => s.status === 'pending').length,
          approved: data.filter(s => s.status === 'approved').length,
          rejected: data.filter(s => s.status === 'rejected').length
        };
      });

      setSubmissions(all);
      setCounts(c);
    } catch (err) {
      // 401/403 means token is invalid or user is not admin — redirect to home
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        navigate('/home');
      }
    }
    setLoading(false);
  }, [token, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) fetchAll();
  }, [token, fetchAll]);

  const toggleFeature = async (type, id, current) => {
    try {
      await axios.patch(`/api/submissions/admin/${type}/${id}/feature`,
        { is_featured: current ? 0 : 1 },
        { headers: authHeader }
      );
      fetchAll();
    } catch (err) {
      console.error('Error toggling feature:', err);
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      await axios.patch(`/api/submissions/admin/${type}/${id}`,
        { status },
        { headers: authHeader }
      );
      fetchAll();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const deleteSubmission = async (type, id) => {
    if (!window.confirm('Delete this submission and its images permanently?')) return;

    setDeletingId(id);
    try {
      await axios.delete(`/api/submissions/admin/${type}/${id}`, {
        headers: authHeader
      });
      await fetchAll();
    } catch (err) {
      console.error('Error deleting submission:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderPhotos = (photosStr) => {
    if (!photosStr) return null;
    try {
      const photos = JSON.parse(photosStr);
      if (photos.length === 0) return null;
      return (
        <div className="admin-photos">
          {photos.map((photo, idx) => (
            <img key={idx} src={getImageUrl(photo)} alt={`Photo ${idx + 1}`} className="admin-photo-thumb" />
          ))}
        </div>
      );
    } catch {
      return null;
    }
  };

  const current = submissions[activeType] || [];

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      <div className="admin-tabs">
        {TYPES.map(type => (
          <button
            key={type}
            className={`admin-tab ${activeType === type ? 'active' : ''}`}
            onClick={() => setActiveType(type)}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
            {counts[type] && (
              <span className="tab-counts">
                ({counts[type].pending} pending)
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="admin-summary">
        {counts[activeType] && (
          <div className="summary-stats">
            <span className="stat stat-total">Total: {counts[activeType].total}</span>
            <span className="stat stat-pending">Pending: {counts[activeType].pending}</span>
            <span className="stat stat-approved">Approved: {counts[activeType].approved}</span>
            <span className="stat stat-rejected">Rejected: {counts[activeType].rejected}</span>
          </div>
        )}
      </div>

      {loading ? (
        <p className="loading-text">Loading...</p>
      ) : (
        <div className="submissions-list">
          {current.length === 0 ? (
            <p className="loading-text">No submissions found.</p>
          ) : (
            current.map(sub => (
              <div key={sub.id} className={`admin-card status-${sub.status}`}>
                <div className="admin-card-info">
                  <h3>{sub.channel_title || sub.account_name || sub.blog_title}</h3>
                  <p className="admin-card-meta">
                    <span className={`status-badge ${sub.status}`}>{sub.status}</span>
                    <span>ID: {sub.id}</span>
                    <span>Email: {sub.email}</span>
                    <span>{new Date(sub.created_at).toLocaleDateString()}</span>
                  </p>
                  {activeType === 'youtube' && sub.subscribers && (
                    <p className="admin-card-detail"><strong>Subscribers:</strong> {sub.subscribers}</p>
                  )}
                  {activeType === 'instagram' && sub.followers && (
                    <p className="admin-card-detail"><strong>Followers:</strong> {sub.followers}</p>
                  )}
                  {activeType === 'blog' && sub.views_per_month && (
                    <p className="admin-card-detail"><strong>Views/Month:</strong> {sub.views_per_month}</p>
                  )}
                  <div className="admin-card-tags">
                    {sub.niche && <span className="tag-badge tag-niche">{sub.niche}</span>}
                    {sub.location && <span className="tag-badge tag-location">{sub.location}</span>}
                  </div>
                  {(sub.channel_url || sub.blog_url || sub.instagram_url) && (
                    <p className="admin-card-detail">
                      <strong>URL:</strong>{' '}
                      <a href={sub.channel_url || sub.blog_url || sub.instagram_url} target="_blank" rel="noopener noreferrer">
                        {sub.channel_url || sub.blog_url || sub.instagram_url}
                      </a>
                    </p>
                  )}
                  {renderPhotos(sub.photos)}
                  {(sub.channel_description || sub.account_description || sub.blog_description) && (
                    <p className="admin-card-desc">
                      {sub.channel_description || sub.account_description || sub.blog_description}
                    </p>
                  )}
                </div>
                <div className="admin-card-actions">
                  {sub.status !== 'approved' && (
                    <button className="action-btn approve" onClick={() => updateStatus(activeType, sub.id, 'approved')}>
                      Approve
                    </button>
                  )}
                  {sub.status !== 'rejected' && (
                    <button className="action-btn reject" onClick={() => updateStatus(activeType, sub.id, 'rejected')}>
                      Reject
                    </button>
                  )}
                  {sub.status !== 'pending' && (
                    <button className="action-btn pending-btn" onClick={() => updateStatus(activeType, sub.id, 'pending')}>
                      Reset to Pending
                    </button>
                  )}
                  {sub.status === 'approved' && (
                    <button
                      className={`action-btn feature-btn ${sub.is_featured ? 'featured' : ''}`}
                      onClick={() => toggleFeature(activeType, sub.id, sub.is_featured)}
                    >
                      {sub.is_featured ? '★ Featured' : '☆ Feature'}
                    </button>
                  )}
                  <button
                    className="action-btn delete-btn"
                    onClick={() => deleteSubmission(activeType, sub.id)}
                    disabled={deletingId === sub.id}
                  >
                    {deletingId === sub.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
